/**
 * 69.4 step 4: a relay or FOB changes hands (BUILDLIST 69.4; the coordinator approved the rule, Oct 12). The points' rule
 * (core/capturePoints.ts stepCapture) asked of a built node of the web:
 * - **held**: one enemy side's living, grounded units stand within the points' capture radius of it and nobody else's
 *   do; at FOB.captureS (RELAY.captureS) seconds for one unit, faster with more, up to POINTS_MAX_CAPTURERS;
 * - **contested = frozen**: any of the owner's units there, or a second enemy side, freezes it with its progress kept
 *   (so does an enemy who sees it, with `contestRange` set; and nobody there — the points keep a part-taken point so);
 *   the owner's units there alone undo it; units under fire (`suppressed`) don't capture, as on a point;
 * - **the flip** has no neutral stage (a structure is somebody's): the owner becomes the first taker on it in the players'
 *   order (`structureTeam` reads the owner, so its side follows), a FOB's crate turns to the taker's side with what it
 *   holds; a relay keeps its drops; the sides' FOB counts (`fobsLeft`) are untouched. Both webs read `structureTeam`: it
 *   is the taker's node, linked if their web reaches it, cut if not — the Match works the webs out at once on a flip — and
 *   the arrivals (core/drops.ts `pushArrivalAnchor`) follow the new owner unchanged.
 *
 * A relay's capture is its own dial (the coordinator: the row promised the FOB's): RELAY.captureS 0 = destroy-only.
 * Standing, owned relays and FOBs only, with GameConfig.relays (the web). No bot seeks one out in this step. Pure.
 *
 * Cost: the Match steps it ten times a second (match/rates.ts `RATES.structureCapture`, its `dt` the beat's): a dot product
 * per living unit per standing relay or FOB. Measured at 100 units: 12 µs a call with 4 nodes, 45 µs with 24 each crowded
 * by a side — ≤ 4 µs a tick averaged over the beat; nothing without GameConfig.relays.
 */
import type { GameState, StructureState } from '../types';
import { FOB, RELAY, POINTS_MAX_CAPTURERS, POINTS_CAPTURE_SUPPRESS_SECONDS } from '../constants';
import { structureTeam } from './body';
import { personSetOf } from './scaleProfile';
import { watchedByEnemy, sectorAt } from './capturePoints';
import { arcDistance } from './gameState';
import { vecNormalize } from '../math';
import { dcos } from './dmath';
import { defaultOn } from './defaultOn';

export type StructureCaptureEvent = { kind: 'structureCaptured'; structureId: number; structure: 'fob' | 'relay'; team: number; fromTeam: number; unitId: number; sector?: number };   // 69.4 step 6d: `sector`, taken with a fallen sector

/** The seconds one unit takes to capture this structure, or 0 when it can't be captured (not a relay or FOB; relays destroy-only). */
export function captureSeconds(s: Pick<StructureState, 'type'>): number {
  return s.type === 'fob' ? FOB.captureS : s.type === 'relay' ? RELAY.captureS : 0;
}

/** One step of every relay's and FOB's capture (`dt` seconds): the structures when any changed, and the flips. Null when nothing did. */
export function stepStructureCapture(state: GameState, dt: number): { structures: StructureState[]; events: StructureCaptureEvent[] } | null {
  if (!state.config.relays) return null;
  const mc = state.config.modeConfig;
  const r = mc.captureRadius ?? personSetOf(state).captureRadius;
  const suppressing = (mc.captureSuppressSeconds ?? POINTS_CAPTURE_SUPPRESS_SECONDS) > 0 || defaultOn(state.config, 'suppression');
  let out: StructureState[] | null = null;
  const events: StructureCaptureEvent[] = [];
  // The units that could stand on one, their directions normalised once (not once a node): living and grounded
  let units: Array<{ x: number; y: number; z: number; planetIndex: number; team: number; able: boolean; id: number }> | null = null;
  state.structures.forEach((s, i) => {
    const seconds = captureSeconds(s);
    if (seconds <= 0 || s.health <= 0) return;
    const owner = structureTeam(state, s);
    const planet = state.planets[s.planetIndex];
    if (owner === null || !planet) return;
    units ??= state.players.filter(p => p.alive && !p.airborne).map(p => {
      const d = vecNormalize(p.surfaceDir);
      return { x: d.x, y: d.y, z: d.z, planetIndex: p.planetIndex, team: p.teamIndex, able: !(suppressing && p.suppressed), id: p.id };
    });
    const at = vecNormalize(s.surfaceDir), near = dcos(r / planet.radius);
    // Who stands on it: each side's count, its units able to capture, and the first of those (the players' order)
    const on = new Map<number, { n: number; able: number; first: number | null }>();
    for (const u of units) {
      if (u.planetIndex !== s.planetIndex || u.x * at.x + u.y * at.y + u.z * at.z < near) continue;
      const c = on.get(u.team) ?? { n: 0, able: 0, first: null };
      c.n++;
      if (u.able) { c.able++; c.first ??= u.id; }
      on.set(u.team, c);
    }
    if (on.size !== 1) return;   // nobody, or contested: frozen
    const [team, c] = [...on][0];
    const set = (next: StructureState) => { (out ??= state.structures.slice())[i] = next; };
    if (team === owner) {   // the owner's alone undo a capture under way
      if (s.capture) { const { capture: _, ...rest } = s; set(rest); }
      return;
    }
    if (mc.contestRange && watchedByEnemy(state, s, team, mc.contestRange)) return;   // seen by an enemy of the takers: frozen
    if (c.able <= 0 || c.first === null) return;   // all under fire: frozen
    const from = s.capture?.team === team ? s.capture.progress : 0;   // another side's partial capture starts over
    const progress = from + Math.min(c.able, POINTS_MAX_CAPTURERS) * dt / seconds;
    if (progress < 1) { set({ ...s, capture: { team, progress } }); return; }
    const { capture: _, ...rest } = s;
    set({ ...rest, ownerId: c.first, ...(s.supply ? { supply: { ...s.supply, teamIndex: team } } : {}) });
    events.push({ kind: 'structureCaptured', structureId: s.id, structure: s.type as 'fob' | 'relay', team, fromTeam: owner, unitId: c.first });
  });
  return out ? { structures: out, events } : null;
}

/**
 * 69.4 step 6d (the step 6 note's (4); DECISIONS 171 Q4's default; with GameConfig.groundRules): ground changing hands at
 * the sector's scale. When sector `sector` falls to `team` (a `sectorTaken`), every standing FOB and relay of another side
 * in it is the taker's — step 4's flip without the wait: its owner the taker's living unit nearest it (else the side's
 * first in the players' order), a FOB's crate turned to the taker's side with what it holds (its store too), a capture under
 * way cleared; a relay keeps its drops. A beacon there is destroyed instead (69.6i). Step 4's capture stays as the local
 * form, for ground still contested. The Match works both webs out at once on a take. Null when nothing was taken.
 */
export function takenWithSector(state: GameState, sector: number, team: number): { structures: StructureState[]; events: StructureCaptureEvent[] } | null {
  const front = state.modeState.points?.front;
  if (!front) return null;
  const planet = state.planets[front.planetIndex];
  const takers = state.players.filter(p => p.teamIndex === team);
  if (!planet || takers.length === 0) return null;
  let out: StructureState[] | null = null;
  const events: StructureCaptureEvent[] = [];
  state.structures.forEach((s, i) => {
    if ((s.type !== 'fob' && s.type !== 'relay') || s.health <= 0 || s.planetIndex !== front.planetIndex || sectorAt(front, s.surfaceDir) !== sector) return;
    const from = structureTeam(state, s);
    if (from === null || from === team) return;
    const alive = takers.filter(p => p.alive && p.planetIndex === s.planetIndex);
    const owner = alive.length
      ? alive.reduce((a, p) => { const da = arcDistance(a.surfaceDir, s.surfaceDir, planet.radius), dp = arcDistance(p.surfaceDir, s.surfaceDir, planet.radius); return dp < da || (dp === da && p.id < a.id) ? p : a; })
      : takers[0];
    const { capture: _, ...rest } = s;
    (out ??= state.structures.slice())[i] = { ...rest, ownerId: owner.id, ...(s.supply ? { supply: { ...s.supply, teamIndex: team } } : {}) };
    events.push({ kind: 'structureCaptured', structureId: s.id, structure: s.type, team, fromTeam: from, unitId: owner.id, sector });
  });
  return out ? { structures: out, events } : null;
}

